import React, { createContext, useContext, useState } from 'react';
import { catalogs, type SupportedLocale } from '@fencom/i18n';

interface I18nContextType {
  locale: SupportedLocale;
  setLocale: (locale: SupportedLocale) => void;
  t: (path: string) => string;
}

const I18nContext = createContext<I18nContextType>({
  locale: 'en',
  setLocale: () => {},
  t: (path: string) => path,
});

export function I18nProvider({ children, initialLocale }: { children: React.ReactNode; initialLocale?: string }) {
  const [locale, setLocaleState] = useState<SupportedLocale>(() => {
    const saved = localStorage.getItem('fencom_locale') as SupportedLocale;
    if (saved && (saved === 'en' || saved === 'am')) return saved;
    if (initialLocale && (initialLocale === 'am' || initialLocale.startsWith('am'))) return 'am';
    return 'en';
  });

  const setLocale = (newLocale: SupportedLocale) => {
    setLocaleState(newLocale);
    localStorage.setItem('fencom_locale', newLocale);
  };

  const t = (path: string): string => {
    const [section, key] = path.split('.');
    if (!section || !key) return path;
    const cat = catalogs[locale] || catalogs.en;
    const sec = (cat as any)[section];
    if (sec && sec[key]) return sec[key];
    const fallbackSec = (catalogs.en as any)[section];
    return fallbackSec?.[key] || key;
  };

  return (
    <I18nContext.Provider value={{ locale, setLocale, t }}>
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n() {
  return useContext(I18nContext);
}
